/**
 * useCallControls Hook
 * Handles mute, speaker, video, screen share, hold, and other call control actions
 */

'use client';

import { useState, useRef, useCallback, useEffect } from 'react';
import { Room, Track, LocalTrack } from 'livekit-client';
import { isCapacitor, isAndroid, isIOS } from '@/lib/platformUtils';
import { CALL_REACTIONS } from '../types';
import type { UseCallControlsReturn, CallReaction } from '../types';
import { authService } from '@/lib/authService';
import { buildApiUrl } from '@/lib/platformUtils';
import toast from 'react-hot-toast';
import { notify } from '@/lib/notifications';

// Native plugins - dynamically imported
let AudioSession: any = null;
let AudioRouter: any = null;
let BackgroundBlur: any = null;

// Initialize native plugins
async function initializePlugins() {
  if (isCapacitor()) {
    // iOS AudioSession
    if (isIOS()) {
      try {
        const asModule = await import('@capgo/capacitor-audio-session');
        AudioSession = asModule.AudioSession;
      } catch (e) {
        console.log('[useCallControls] AudioSession not available');
      }
    }
    
    // Android AudioRouter
    if (isAndroid()) {
      try {
        const arModule = await import('@/plugins/AudioRouter');
        AudioRouter = arModule.AudioRouter;
      } catch (e) {
        console.log('[useCallControls] AudioRouter not available');
      }
    }
  }
  
  // Background blur for web/desktop
  if (!isCapacitor()) {
    try {
      const blurModule = await import('@livekit/track-processors');
      BackgroundBlur = blurModule.BackgroundBlur;
    } catch (e) {
      console.log('[useCallControls] BackgroundBlur not available');
    }
  }
}

// Initialize on module load
if (typeof window !== 'undefined') {
  initializePlugins();
}

function getErrorName(error: unknown): string {
  if (error instanceof Error) {
    return error.name;
  }

  if (error && typeof error === 'object' && 'name' in error) {
    return String((error as Record<string, unknown>).name ?? '');
  }

  return '';
}

interface UseCallControlsOptions {
  roomRef: React.MutableRefObject<Room | null>;
  wsRef: React.MutableRefObject<WebSocket | null>;
  callIdRef: React.MutableRefObject<string | null>;
  sipCallData?: { call_log_id?: string; livekit_room?: string; client_id?: number } | null;
  /** When set (e.g. dual-line focus), hold API uses this leg at invoke time instead of sipCallData/callIdRef only */
  sipHoldLegRef?: React.MutableRefObject<{
    call_log_id?: string;
    livekit_room?: string;
    client_id?: number;
    callId: string | null;
  } | null>;
  user?: { id?: number; first_name?: string; last_name?: string } | null;
  onHapticFeedback?: (style: 'light' | 'medium' | 'heavy') => void;
}

interface BrowserAudioOutputTargets {
  speakerSinkId: string;
  earpieceSinkId: string | null;
  systemDefaultSinkId: string;
}

type SinkAssignableAudioElement = HTMLAudioElement & {
  setSinkId?: (deviceId: string) => Promise<void>;
  sinkId?: string;
};

function normalizeBrowserSinkId(sinkId: string | null | undefined): string | null {
  if (typeof sinkId !== 'string') {
    return null;
  }

  const trimmed = sinkId.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function getAudioOutputLabel(device: MediaDeviceInfo): string {
  return device.label.toLowerCase();
}

function scoreSpeakerAudioOutput(device: MediaDeviceInfo): number {
  const label = getAudioOutputLabel(device);
  let score = 0;

  if (device.deviceId === 'communications' || label.includes('communications')) {
    score -= 200;
  }

  if (label.includes('speaker') || label.includes('speakerphone')) {
    score += 120;
  }

  if (
    label.includes('built-in')
    || label.includes('internal')
    || label.includes('realtek')
    || label.includes('display audio')
  ) {
    score += 60;
  }

  if (device.deviceId === 'default') {
    score += 40;
  }

  if (
    label.includes('headphone')
    || label.includes('headset')
    || label.includes('earpiece')
    || label.includes('receiver')
    || label.includes('handset')
    || label.includes('bluetooth')
    || label.includes('airpods')
    || label.includes('buds')
  ) {
    score -= 80;
  }

  return score;
}

function scoreEarpieceAudioOutput(device: MediaDeviceInfo): number {
  const label = getAudioOutputLabel(device);
  let score = 0;

  if (device.deviceId === 'communications') {
    score += 220;
  }

  if (label.includes('communications')) {
    score += 180;
  }

  if (
    label.includes('earpiece')
    || label.includes('receiver')
    || label.includes('handset')
  ) {
    score += 140;
  }

  if (
    label.includes('headphone')
    || label.includes('headset')
    || label.includes('bluetooth')
    || label.includes('airpods')
    || label.includes('buds')
  ) {
    score += 120;
  }

  if (device.deviceId === 'default') {
    score += 30;
  }

  if (
    label.includes('speaker')
    || label.includes('speakerphone')
    || label.includes('built-in')
    || label.includes('internal')
    || label.includes('realtek')
  ) {
    score -= 60;
  }

  return score;
}

function pickBestAudioOutput(
  devices: MediaDeviceInfo[],
  scoreDevice: (device: MediaDeviceInfo) => number,
  excludeSinkId?: string | null,
): string | null {
  const excludeId = normalizeBrowserSinkId(excludeSinkId);
  const ranked = devices
    .filter((device) => normalizeBrowserSinkId(device.deviceId) !== excludeId)
    .map((device) => ({
      deviceId: normalizeBrowserSinkId(device.deviceId),
      score: scoreDevice(device),
    }))
    .filter((entry): entry is { deviceId: string; score: number } => entry.deviceId !== null)
    .sort((a, b) => b.score - a.score);

  return ranked[0]?.deviceId ?? null;
}

export function useCallControls({
  roomRef,
  wsRef,
  callIdRef,
  sipCallData,
  sipHoldLegRef,
  user,
  onHapticFeedback,
}: UseCallControlsOptions): UseCallControlsReturn {
  const [canToggleSpeaker, setCanToggleSpeaker] = useState(() => {
    if (isCapacitor()) {
      return true;
    }

    if (typeof window === 'undefined') {
      return false;
    }

    return typeof (HTMLMediaElement.prototype as HTMLMediaElement & {
      setSinkId?: (deviceId: string) => Promise<void>;
    }).setSinkId === 'function';
  });

  // State
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoEnabled, setIsVideoEnabled] = useState(false);
  const [isSpeakerOn, setIsSpeakerOn] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [isBlurEnabled, setIsBlurEnabled] = useState(false);
  const [isOnHold, setIsOnHold] = useState(false);
  const [isHandRaised, setIsHandRaised] = useState(false);
  const [isFrontCamera, setIsFrontCamera] = useState(true);
  const [audioOutputDevice, setAudioOutputDevice] = useState<'speaker' | 'earpiece' | 'bluetooth'>('earpiece');
  
  // Refs
  const screenShareTrackRef = useRef<LocalTrack | MediaStreamTrack | null>(null);
  const blurProcessorRef = useRef<unknown>(null);
  const browserAudioTargetsRef = useRef<BrowserAudioOutputTargets | null>(null);
  const browserPreviousSinkIdRef = useRef<string | null>(null);
  const browserCurrentSinkIdRef = useRef<string | null>(null);

  const browserSupportsAudioRouting = useCallback(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    return typeof (HTMLMediaElement.prototype as HTMLMediaElement & {
      setSinkId?: (deviceId: string) => Promise<void>;
    }).setSinkId === 'function';
  }, []);

  const getRemoteAudioElements = useCallback((): SinkAssignableAudioElement[] => {
    if (typeof document === 'undefined') {
      return [];
    }

    return Array.from(
      document.querySelectorAll('audio[data-total-connect-remote-audio="true"]')
    ) as SinkAssignableAudioElement[];
  }, []);

  const loadBrowserAudioOutputTargets = useCallback(async (): Promise<BrowserAudioOutputTargets | null> => {
    if (!browserSupportsAudioRouting()) {
      browserAudioTargetsRef.current = null;
      return null;
    }

    const fallbackTargets = {
      speakerSinkId: 'default',
      earpieceSinkId: 'communications',
      systemDefaultSinkId: 'default',
    } satisfies BrowserAudioOutputTargets;

    if (!navigator.mediaDevices?.enumerateDevices) {
      browserAudioTargetsRef.current = fallbackTargets;
      browserPreviousSinkIdRef.current ??= fallbackTargets.earpieceSinkId;
      browserCurrentSinkIdRef.current ??= fallbackTargets.earpieceSinkId;
      return fallbackTargets;
    }

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioOutputs = devices.filter((device) => device.kind === 'audiooutput');

      if (audioOutputs.length === 0) {
        browserAudioTargetsRef.current = fallbackTargets;
        browserPreviousSinkIdRef.current ??= fallbackTargets.earpieceSinkId;
        browserCurrentSinkIdRef.current ??= fallbackTargets.earpieceSinkId;
        return fallbackTargets;
      }

      const systemDefaultSinkId =
        normalizeBrowserSinkId(
          audioOutputs.find((device) => device.deviceId === 'default')?.deviceId
        )
        ?? normalizeBrowserSinkId(audioOutputs[0]?.deviceId)
        ?? fallbackTargets.systemDefaultSinkId;

      const speakerSinkId =
        pickBestAudioOutput(audioOutputs, scoreSpeakerAudioOutput)
        ?? systemDefaultSinkId;

      const earpieceSinkId =
        pickBestAudioOutput(audioOutputs, scoreEarpieceAudioOutput, speakerSinkId)
        ?? normalizeBrowserSinkId(
          audioOutputs.find((device) => device.deviceId === 'communications')?.deviceId
        )
        ?? systemDefaultSinkId;

      const targets = {
        speakerSinkId,
        earpieceSinkId,
        systemDefaultSinkId,
      } satisfies BrowserAudioOutputTargets;

      const currentSinkId =
        normalizeBrowserSinkId(getRemoteAudioElements()[0]?.sinkId)
        ?? browserCurrentSinkIdRef.current
        ?? earpieceSinkId
        ?? systemDefaultSinkId;

      browserPreviousSinkIdRef.current ??= currentSinkId;
      browserCurrentSinkIdRef.current = currentSinkId;
      browserAudioTargetsRef.current = targets;
      return targets;
    } catch (error) {
      console.warn('[useCallControls] Could not enumerate browser audio outputs:', error);
      browserAudioTargetsRef.current = fallbackTargets;
      browserPreviousSinkIdRef.current ??= fallbackTargets.earpieceSinkId;
      browserCurrentSinkIdRef.current ??= fallbackTargets.earpieceSinkId;
      return fallbackTargets;
    }
  }, [browserSupportsAudioRouting, getRemoteAudioElements]);

  useEffect(() => {
    if (isCapacitor()) {
      return;
    }

    let cancelled = false;

    const refreshRoutingSupport = async () => {
      await loadBrowserAudioOutputTargets();
      if (!cancelled) {
        setCanToggleSpeaker(true);
      }
    };

    void refreshRoutingSupport();

    const handleDeviceChange = () => {
      void refreshRoutingSupport();
    };

    navigator.mediaDevices?.addEventListener?.('devicechange', handleDeviceChange);

    return () => {
      cancelled = true;
      navigator.mediaDevices?.removeEventListener?.('devicechange', handleDeviceChange);
    };
  }, [browserSupportsAudioRouting, loadBrowserAudioOutputTargets]);

  const refreshRemoteAudioPlayback = useCallback(async (sinkId?: string): Promise<boolean> => {
    const room = roomRef.current;
    let roomSinkApplied = sinkId === undefined;

    if (room) {
      const switchActiveDevice = (room as Room & {
        switchActiveDevice?: (kind: MediaDeviceKind, deviceId: string) => Promise<void>;
      }).switchActiveDevice;

      if (typeof switchActiveDevice === 'function' && sinkId && sinkId.length > 0) {
        try {
          await switchActiveDevice.call(room, 'audiooutput', sinkId);
          roomSinkApplied = true;
        } catch (error) {
          console.log('[useCallControls] Default audio output reset skipped:', error);
        }
      }

      try {
        await room.startAudio();
      } catch (error) {
        console.log('[useCallControls] room.startAudio() deferred during speaker sync:', error);
      }
    }

    if (typeof document === 'undefined') {
      return roomSinkApplied;
    }

    const remoteAudioElements = getRemoteAudioElements();

    const playbackResults = await Promise.allSettled(
      remoteAudioElements.map(async (audioEl) => {
        audioEl.volume = 1.0;
        audioEl.muted = false;
        audioEl.autoplay = true;
        audioEl.setAttribute('playsinline', 'true');
        audioEl.setAttribute('webkit-playsinline', 'true');

        let elementSinkApplied = sinkId === undefined;

        const audioElWithSink = audioEl as HTMLAudioElement & {
          setSinkId?: (deviceId: string) => Promise<void>;
        };

        if (sinkId !== undefined && typeof audioElWithSink.setSinkId === 'function') {
          try {
            await audioElWithSink.setSinkId(sinkId);
            elementSinkApplied = true;
          } catch {
            elementSinkApplied = false;
          }
        }

        await audioEl.play().catch(() => {});
        return elementSinkApplied;
      })
    );

    const elementSinkApplied = playbackResults.some(
      (result) => result.status === 'fulfilled' && result.value
    );

    return sinkId === undefined ? true : roomSinkApplied || elementSinkApplied;
  }, [getRemoteAudioElements, roomRef]);

  const applyBrowserAudioRoute = useCallback(async (enabled: boolean) => {
    const targets = browserAudioTargetsRef.current ?? await loadBrowserAudioOutputTargets();

    if (!targets) {
      return false;
    }

    const remoteAudioElements = getRemoteAudioElements();
    const firstRemoteAudio = remoteAudioElements[0];
    const currentSinkId =
      normalizeBrowserSinkId(firstRemoteAudio?.sinkId)
      ?? browserCurrentSinkIdRef.current
      ?? browserPreviousSinkIdRef.current
      ?? targets.earpieceSinkId
      ?? targets.systemDefaultSinkId;

    if (enabled) {
      browserPreviousSinkIdRef.current =
        currentSinkId && currentSinkId !== targets.speakerSinkId
          ? currentSinkId
          : browserPreviousSinkIdRef.current
            ?? targets.earpieceSinkId
            ?? targets.systemDefaultSinkId;
    }

    const targetSinkId = normalizeBrowserSinkId(
      enabled
        ? targets.speakerSinkId
        : browserPreviousSinkIdRef.current
          ?? targets.earpieceSinkId
          ?? targets.systemDefaultSinkId
    );

    if (!targetSinkId) {
      return false;
    }

    if (currentSinkId === targetSinkId) {
      return false;
    }

    const sinkApplied = await refreshRemoteAudioPlayback(targetSinkId);

    if (!sinkApplied) {
      return false;
    }

    browserCurrentSinkIdRef.current = targetSinkId;
    setIsSpeakerOn(enabled);
    setAudioOutputDevice(enabled ? 'speaker' : 'earpiece');

    return true;
  }, [getRemoteAudioElements, loadBrowserAudioOutputTargets, refreshRemoteAudioPlayback]);
  
  /**
   * Set speaker mode
   */
  const setSpeakerOn = useCallback(async (enabled: boolean) => {
    try {
      if (AudioRouter && isAndroid()) {
        await AudioRouter.setSpeakerphone({ speaker: enabled });
        console.log('[useCallControls] Android speaker:', enabled);
        setIsSpeakerOn(enabled);
        setAudioOutputDevice(enabled ? 'speaker' : 'earpiece');
        return;
      }

      if (AudioSession && isIOS()) {
        if (enabled) {
          await AudioSession.overrideOutputAudioPort({ port: 'speaker' });
        } else {
          await AudioSession.overrideOutputAudioPort({ port: 'none' });
        }
        console.log('[useCallControls] iOS speaker:', enabled);
        setIsSpeakerOn(enabled);
        setAudioOutputDevice(enabled ? 'speaker' : 'earpiece');
        return;
      }

      // Web/Tauri fallback: browsers do not reliably expose a true earpiece vs speaker route.
      // Reset playback to the default sink and re-prime active remote audio instead of
      // guessing a hardware output by label, which can strand short TTS clips mid-call.
      await refreshRemoteAudioPlayback();
      console.log('[useCallControls] Web/Tauri speaker toggle synced to default audio output');

      setIsSpeakerOn(enabled);
      setAudioOutputDevice(enabled ? 'speaker' : 'earpiece');
    } catch (e) {
      console.error('[useCallControls] Speaker toggle error:', e);
    }
  }, [refreshRemoteAudioPlayback]);

  /**
   * Toggle microphone mute
   */
  const toggleMute = useCallback(async () => {
    onHapticFeedback?.('light');
    
    if (!roomRef.current) return;
    
    try {
      const localParticipant = roomRef.current.localParticipant;
      const newMutedState = !isMuted;
      
      await localParticipant.setMicrophoneEnabled(!newMutedState);
      setIsMuted(newMutedState);
      
      console.log('[useCallControls] Microphone', newMutedState ? 'muted' : 'unmuted');
    } catch (err) {
      console.error('[useCallControls] Error toggling mute:', err);
      toast.error('Failed to toggle mute', { duration: 3000 });
    }
  }, [isMuted, roomRef, onHapticFeedback]);
  
  /**
   * Toggle video camera
   */
  const toggleVideo = useCallback(async () => {
    onHapticFeedback?.('light');
    
    if (!roomRef.current) return;
    
    try {
      const localParticipant = roomRef.current.localParticipant;
      
      if (isVideoEnabled) {
        // Disable video - no confirmation needed
        if (isBlurEnabled) {
          try {
            const cameraPublication = localParticipant.getTrackPublication(Track.Source.Camera);
            const videoTrack = cameraPublication?.track;
            if (videoTrack) {
              await videoTrack.stopProcessor();
            }
          } catch (e) {
            console.log('[useCallControls] Error stopping blur processor:', e);
          }
          blurProcessorRef.current = null;
          setIsBlurEnabled(false);
        }
        
        await localParticipant.setCameraEnabled(false);
        setIsVideoEnabled(false);
        console.log('[useCallControls] Video disabled');
      } else {
        // Enable video - show confirmation first
        const confirmed = await notify.confirm({
          title: 'Enable Video',
          message: 'This will turn on your camera and make this a video call. The other participant will be able to see you.',
          confirmText: 'Turn On Camera',
          cancelText: 'Cancel',
          variant: 'info',
          onConfirm: () => {}
        });
        
        if (!confirmed) {
          console.log('[useCallControls] Video enable cancelled by user');
          return;
        }
        
        // User confirmed - enable video
        await localParticipant.setCameraEnabled(true);
        setIsVideoEnabled(true);
        console.log('[useCallControls] Video enabled');
        
        // Auto-switch to speaker for video calls on mobile
        if (isCapacitor() && !isSpeakerOn) {
          console.log('[useCallControls] Video enabled - auto-switching to speaker');
          await setSpeakerOn(true);
        }
      }
    } catch (err: unknown) {
      console.error('[useCallControls] Toggle video error:', err);
      toast.error('Could not access camera', { duration: 3000 });
    }
  }, [isVideoEnabled, isBlurEnabled, isSpeakerOn, roomRef, onHapticFeedback, setSpeakerOn]);
  
  /**
   * Toggle speaker/earpiece
   */
  const toggleSpeaker = useCallback(async () => {
    onHapticFeedback?.('light');

    if (!isCapacitor()) {
      const switched = await applyBrowserAudioRoute(!isSpeakerOn);

      if (!switched) {
        await refreshRemoteAudioPlayback();
        toast('Could not change audio output on this device', {
          duration: 3000,
          icon: '🔊',
        });
        return;
      }

      toast.success(`Audio: ${!isSpeakerOn ? 'Speaker' : 'Earpiece'}`, { duration: 2000 });
      return;
    }

    await setSpeakerOn(!isSpeakerOn);
    toast.success(`Audio: ${!isSpeakerOn ? 'Speaker' : 'Earpiece'}`, { duration: 2000 });
  }, [applyBrowserAudioRoute, isSpeakerOn, onHapticFeedback, refreshRemoteAudioPlayback, setSpeakerOn]);
  
  /**
   * Toggle screen sharing (web/desktop only)
   */
  const toggleScreenShare = useCallback(async () => {
    if (isCapacitor()) {
      toast('Screen sharing is only available on web', { duration: 2000, icon: '💻' });
      return;
    }
    
    if (!roomRef.current) return;
    
    onHapticFeedback?.('light');
    
    try {
      if (isScreenSharing) {
        // Stop screen sharing
        if (screenShareTrackRef.current) {
          await roomRef.current.localParticipant.unpublishTrack(screenShareTrackRef.current);
          screenShareTrackRef.current.stop();
          screenShareTrackRef.current = null;
        }
        setIsScreenSharing(false);
        console.log('[useCallControls] Screen sharing stopped');
        toast.success('Screen sharing stopped', { duration: 2000 });
      } else {
        // Start screen sharing
        const stream = await navigator.mediaDevices.getDisplayMedia({
          video: {
            width: { ideal: 4096 },
            height: { ideal: 2160 },
            frameRate: { ideal: 30, max: 30 },
          },
          audio: false,
        });
        
        const screenTrack = stream.getVideoTracks()[0];
        
        // Handle when user stops sharing via browser UI
        screenTrack.onended = () => {
          setIsScreenSharing(false);
          screenShareTrackRef.current = null;
          toast('Screen sharing ended', { duration: 2000, icon: '🖥️' });
        };
        
        // Publish the screen share track
        await roomRef.current.localParticipant.publishTrack(screenTrack, {
          name: 'screen',
          source: Track.Source.ScreenShare,
          simulcast: false,
          videoEncoding: {
            maxBitrate: 4_000_000,
            maxFramerate: 30,
            priority: 'high'
          },
          videoCodec: 'vp8',
          degradationPreference: 'maintain-resolution'
        });
        
        screenShareTrackRef.current = screenTrack;
        setIsScreenSharing(true);
        console.log('[useCallControls] Screen sharing started');
        toast.success('Screen sharing started', { duration: 2000 });
      }
    } catch (err: unknown) {
      console.error('[useCallControls] Screen share error:', err);
      if (getErrorName(err) === 'NotAllowedError') {
        toast.error('Screen sharing permission denied', { duration: 3000 });
      } else {
        toast.error('Could not share screen', { duration: 3000 });
      }
    }
  }, [isScreenSharing, roomRef, onHapticFeedback]);
  
  /**
   * Toggle background blur (web/desktop only)
   */
  const toggleBlur = useCallback(async () => {
    if (isCapacitor()) {
      toast('Background blur is only available on web/desktop', { duration: 2000, icon: '💻' });
      return;
    }
    
    if (!roomRef.current || !isVideoEnabled) {
      toast('Enable camera first to use background blur', { duration: 2000, icon: '📷' });
      return;
    }
    
    if (!BackgroundBlur) {
      toast.error('Background blur not available', { duration: 2000 });
      return;
    }
    
    onHapticFeedback?.('light');
    
    try {
      const localParticipant = roomRef.current.localParticipant;
      const cameraPublication = localParticipant.getTrackPublication(Track.Source.Camera);
      const videoTrack = cameraPublication?.track;
      
      if (!videoTrack) {
        toast.error('No camera track found', { duration: 2000 });
        return;
      }
      
      if (isBlurEnabled) {
        // Disable blur
        await videoTrack.stopProcessor();
        blurProcessorRef.current = null;
        setIsBlurEnabled(false);
        console.log('[useCallControls] Background blur disabled');
        toast.success('Background blur off', { duration: 2000 });
      } else {
        // Enable blur
        const blurProcessor = BackgroundBlur(15);
        blurProcessorRef.current = blurProcessor;
        await videoTrack.setProcessor(blurProcessor);
        setIsBlurEnabled(true);
        console.log('[useCallControls] Background blur enabled');
        toast.success('Background blur on', { duration: 2000 });
      }
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : err && typeof err === 'object' && 'message' in err
            ? String((err as { message: unknown }).message)
            : '';
      if (message) {
        console.warn('[useCallControls] Toggle blur failed:', message);
      } else {
        console.warn('[useCallControls] Toggle blur failed:', err);
      }
      toast.error('Could not toggle background blur', { duration: 3000 });
    }
  }, [isBlurEnabled, isVideoEnabled, roomRef, onHapticFeedback]);

  const setRemoteAudioMutedForHold = useCallback((targetRoom: Room | null, muted: boolean) => {
    if (!targetRoom) return;

    for (const participant of targetRoom.remoteParticipants.values()) {
      participant.audioTrackPublications.forEach((publication) => {
        const mediaTrack = publication.track?.mediaStreamTrack;
        if (mediaTrack) {
          mediaTrack.enabled = !muted;
        }
      });
    }
  }, []);
  
  /**
   * Toggle call hold (for SIP calls)
   */
  const toggleHold = useCallback(async () => {
    onHapticFeedback?.('medium');
    
    if (!roomRef.current) return;
    
    try {
      const localParticipant = roomRef.current.localParticipant;
      const newHoldState = !isOnHold;
      
      const holdLeg = sipHoldLegRef?.current;
      const holdCallLogId = holdLeg?.call_log_id ?? sipCallData?.call_log_id;
      const holdRoomName =
        holdLeg?.livekit_room ?? sipCallData?.livekit_room ?? holdLeg?.callId ?? callIdRef.current;
      const holdClientId = holdLeg?.client_id ?? sipCallData?.client_id;
      const isSipHoldContext = Boolean(holdCallLogId || holdRoomName);

      // For SIP calls, call the hold API
      if (holdCallLogId) {
        const tokens = authService.getTokens();
        if (tokens?.access_token) {
          const roomName = holdRoomName;
          const holdRes = await fetch(buildApiUrl('/api/v1/sip'), {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${tokens.access_token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              action: 'holdCall',
              call_id: roomName,
              call_log_id: holdCallLogId,
              client_id: holdClientId,
              hold: newHoldState
            })
          });
          
          const holdData = await holdRes.json();
          console.log('[useCallControls] Hold API response:', holdData);
          
          if (!holdData.success) {
            toast.error(holdData.error || 'Failed to toggle hold', { duration: 3000 });
            return;
          }
        }
      }
      
      if (newHoldState) {
        // Put call on hold - disable all outgoing media
        await localParticipant.setMicrophoneEnabled(false);
        if (isVideoEnabled) {
          await localParticipant.setCameraEnabled(false);
        }
        if (isSipHoldContext) {
          setRemoteAudioMutedForHold(roomRef.current, true);
        }
        console.log('[useCallControls] Call placed on hold');
        toast.success('Call on hold - caller hears hold music', { duration: 2000 });
      } else {
        // Resume call
        await localParticipant.setMicrophoneEnabled(true);
        if (isVideoEnabled) {
          await localParticipant.setCameraEnabled(true);
        }
        if (isSipHoldContext) {
          setRemoteAudioMutedForHold(roomRef.current, false);
        }
        console.log('[useCallControls] Call resumed from hold');
        toast.success('Call resumed', { duration: 2000 });
      }
      
      setIsOnHold(newHoldState);
      setIsMuted(newHoldState);
    } catch (err) {
      console.error('[useCallControls] Error toggling hold:', err);
      toast.error('Failed to toggle hold', { duration: 3000 });
    }
  }, [isOnHold, isVideoEnabled, roomRef, sipCallData, sipHoldLegRef, callIdRef, onHapticFeedback, setRemoteAudioMutedForHold]);

  /**
   * Resync mute + hold indicators from the focused room (e.g. dual-line rail swap).
   */
  const syncLocalParticipantUiFromRoom = useCallback(
    (targetRoom: Room | null, isOnHoldOverride: boolean) => {
      const lp =
        targetRoom?.localParticipant ?? roomRef.current?.localParticipant;
      if (lp) {
        setIsMuted(!lp.isMicrophoneEnabled);
      }
      setIsOnHold(isOnHoldOverride);
    },
    [roomRef],
  );

  /**
   * Toggle hand raise
   */
  const toggleHandRaise = useCallback(() => {
    onHapticFeedback?.('medium');
    
    const newState = !isHandRaised;
    setIsHandRaised(newState);
    
    // Send to other participants via WebSocket
    if (wsRef.current?.readyState === WebSocket.OPEN && callIdRef.current) {
      wsRef.current.send(JSON.stringify({
        type: 'call_hand_raise',
        call_id: callIdRef.current,
        user_id: user?.id,
        user_name: `${user?.first_name || ''} ${user?.last_name || ''}`.trim(),
        is_raised: newState
      }));
    }
    
    toast(newState ? '✋ Hand raised' : 'Hand lowered', { duration: 2000 });
  }, [isHandRaised, wsRef, callIdRef, user, onHapticFeedback]);
  
  /**
   * Switch between front and back camera
   */
  const switchCamera = useCallback(async () => {
    onHapticFeedback?.('light');
    
    const room = roomRef.current;

    if (!room?.localParticipant) {
      console.warn('[useCallControls] No room or local participant for camera switch');
      toast.error('Camera not available', { duration: 2000 });
      return;
    }
    
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(d => d.kind === 'videoinput');
      
      if (videoDevices.length < 2) {
        toast.error('Only one camera available', { duration: 2000 });
        return;
      }
      
      // Get current video track
      const videoPublication = Array.from(
        room.localParticipant.videoTrackPublications.values()
      ).find(pub => pub.track && pub.source === Track.Source.Camera);
      
      const currentTrack = videoPublication?.track;
      const currentDeviceId = currentTrack?.mediaStreamTrack?.getSettings()?.deviceId;
      
      // Find target device
      let targetDevice: MediaDeviceInfo | undefined;
      const isFrontCurrent = videoDevices.some(d => 
        d.deviceId === currentDeviceId && 
        (d.label.toLowerCase().includes('front') || d.label.toLowerCase().includes('user'))
      );
      
      if (isFrontCurrent) {
        targetDevice = videoDevices.find(d => 
          d.label.toLowerCase().includes('back') || 
          d.label.toLowerCase().includes('environment') ||
          d.label.toLowerCase().includes('rear')
        ) || videoDevices.find(d => d.deviceId !== currentDeviceId);
      } else {
        targetDevice = videoDevices.find(d => 
          d.label.toLowerCase().includes('front') || 
          d.label.toLowerCase().includes('user')
        ) || videoDevices.find(d => d.deviceId !== currentDeviceId);
      }
      
      if (!targetDevice) {
        toast.error('Could not find other camera', { duration: 2000 });
        return;
      }
      
      // Use Room.switchActiveDevice if available
      const switchActiveDevice = (room as Room & {
        switchActiveDevice?: (kind: MediaDeviceKind, deviceId: string) => Promise<void>;
      }).switchActiveDevice;

      if (typeof switchActiveDevice === 'function') {
        await switchActiveDevice.call(room, 'videoinput', targetDevice.deviceId);
      } else {
        // Fallback: re-publish
        await room.localParticipant.setCameraEnabled(false);
        await new Promise(resolve => setTimeout(resolve, 100));
        await room.localParticipant.setCameraEnabled(true, {
          deviceId: targetDevice.deviceId
        });
      }
      
      const newIsFront = targetDevice.label.toLowerCase().includes('front') || 
                        targetDevice.label.toLowerCase().includes('user');
      setIsFrontCamera(newIsFront);
      toast.success(`Camera: ${newIsFront ? 'Front' : 'Back'}`, { duration: 2000 });
    } catch (err) {
      console.error('[useCallControls] Camera switch error:', err);
      toast.error('Could not switch camera', { duration: 2000 });
    }
  }, [roomRef, onHapticFeedback]);
  
  /**
   * Send a reaction emoji
   */
  const sendReaction = useCallback((emoji: string) => {
    onHapticFeedback?.('light');
    
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN || !callIdRef.current) {
      return;
    }
    
    const reactionId = `${user?.id}-${Date.now()}`;
    const userName = `${user?.first_name || ''} ${user?.last_name || ''}`.trim();
    
    // Send to other participants
    wsRef.current.send(JSON.stringify({
      type: 'call_reaction',
      call_id: callIdRef.current,
      user_id: user?.id,
      user_name: userName,
      emoji,
      reaction_id: reactionId
    }));
  }, [wsRef, callIdRef, user, onHapticFeedback]);
  
  /**
   * Enable audio (for autoplay blocked)
   */
  const enableAudio = useCallback(async () => {
    if (roomRef.current) {
      try {
        await roomRef.current.startAudio();
      } catch (err) {
        console.error('[useCallControls] Enable audio error:', err);
      }
    }
  }, [roomRef]);
  
  return {
    // State
    isMuted,
    isVideoEnabled,
    isSpeakerOn,
    canToggleSpeaker,
    isScreenSharing,
    isBlurEnabled,
    isOnHold,
    isHandRaised,
    isFrontCamera,
    audioOutputDevice,
    
    // Actions
    toggleMute,
    toggleVideo,
    toggleSpeaker,
    toggleScreenShare,
    toggleBlur,
    toggleHold,
    syncLocalParticipantUiFromRoom,
    toggleHandRaise,
    switchCamera,
    sendReaction,
    enableAudio,
    /** Allow external call to sync video-enabled state (e.g. after connectToRoom publishes video) */
    setVideoEnabled: setIsVideoEnabled,
  };
}

export default useCallControls;
